import { parseOhlcRows, medianStepMs } from './intradayCandles';
import type { OhlcBar, ChartCandle } from './intradayCandles';

/**
 * Real daily candles for the coin chart's 1D (and 1W) view. Pure, so the
 * hand-over between sources can be tested.
 *
 * ── Why two sources ──────────────────────────────────────────────────────────
 *
 * The ~2-year daily `market_chart` has ONE price per day, so a daily candle built
 * from it has no wicks (high and low are just its open and close).
 * `/coins/{id}/ohlc?interval=daily` returns true daily OHLC on paid plans, but
 * only for up to 180 days (hourly OHLC, the other candidate, reaches 90). So the
 * newest 180 days use the real OHLC and older days keep the market_chart candles,
 * and 1D still reaches back ~2 years.
 *
 * Both are dated by UTC day, keyed by the day's 00:00 UTC in seconds. CoinGecko
 * stamps each OHLC candle at its CLOSE (the next 00:00 UTC); parseOhlcRows moves
 * it back to the day it covers. Days are merged by key with the OHLC taking
 * precedence, so a day can never appear twice.
 */

export const DAY_MS = 86_400_000;
export const DAY_SEC = 86_400;

/** How many days of real daily OHLC to request (the paid plan's maximum). */
export const DAILY_OHLC_DAYS = 180;

/**
 * `/ohlc?interval=daily` rows as daily bars, or null if the response is not
 * daily (a plan without the interval returns 4-day candles instead).
 */
export function parseDailyOhlc(rows: unknown): OhlcBar[] | null {
  if (!Array.isArray(rows) || rows.length < 2) return null;
  const step = medianStepMs(rows as number[][]);
  if (!(step >= DAY_MS * 0.9 && step <= DAY_MS * 1.1)) return null;
  const bars = parseOhlcRows(rows, DAY_MS);
  return bars.length > 0 ? bars : null;
}

/**
 * Daily candles from both sources: the real OHLC for every day it covers, the
 * market_chart candles for every other day (older history, today if the OHLC
 * does not include it yet, and any day missing from it).
 *
 * The join is made exact: a market_chart day's single price is that day's
 * 00:00 UTC snapshot, so the day just before the OHLC starts closes on the
 * first OHLC open instead (high / low widened to keep it valid). Without that,
 * the first OHLC candle would carry two days of movement once chainOpens ran.
 */
export function mergeDailyCandles<T extends ChartCandle>(
  fallback: readonly T[],
  ohlc: readonly ChartCandle[] | null,
): T[] {
  if (!ohlc || ohlc.length === 0) return fallback as T[];
  const byDay = new Map<number, T>();
  for (const c of fallback) byDay.set(c.time, c);
  for (const c of ohlc) byDay.set(c.time, { ...(byDay.get(c.time) ?? {}), ...c } as T);
  const out = Array.from(byDay.values()).sort((a, b) => a.time - b.time);

  const firstOhlc = ohlc.reduce((a, b) => (b.time < a.time ? b : a));
  const i = out.findIndex((c) => c.time === firstOhlc.time);
  const before = out[i - 1];
  if (i > 0 && before.time === firstOhlc.time - DAY_SEC) {
    const close = firstOhlc.open;
    out[i - 1] = { ...before, close, high: Math.max(before.high, close), low: Math.min(before.low, close) };
  }
  return out;
}

/**
 * Weekly candles from daily ones, so a week's wick is the true high / low of
 * its days. Open is the first day's open, close the last day's close.
 */
export function weeklyFromDaily<T extends ChartCandle>(
  daily: readonly T[],
  weekStartSec: (ms: number) => number,
): T[] {
  const weeks = new Map<number, T>();
  for (const d of [...daily].sort((a, b) => a.time - b.time)) {
    const key = weekStartSec(d.time * 1000);
    const w = weeks.get(key);
    if (!w) weeks.set(key, { ...d, time: key });
    else weeks.set(key, { ...w, high: Math.max(w.high, d.high), low: Math.min(w.low, d.low), close: d.close });
  }
  return Array.from(weeks.values()).sort((a, b) => a.time - b.time);
}

export interface VolumeBar { time: number; value: number; color: string }

/**
 * Volume bars for `candles`, matched by day (or week) key: one bar per candle
 * that has a volume, none for a candle without one, and none left over. Each
 * bar is coloured by its candle as the chart draws it (chained: up when the
 * close is at or above the previous close).
 */
export function matchVolumes(
  candles: readonly ChartCandle[],
  volumes: readonly { time: number; value: number }[],
  upColor: string,
  downColor: string,
): VolumeBar[] {
  const byTime = new Map(volumes.map((v) => [v.time, v.value]));
  const out: VolumeBar[] = [];
  candles.forEach((c, i) => {
    const value = byTime.get(c.time);
    if (value === undefined) return;
    const open = i > 0 ? candles[i - 1].close : c.open;
    out.push({ time: c.time, value, color: c.close >= open ? upColor : downColor });
  });
  return out;
}
